import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { clinicalApi, PatientDetailItem } from '../../services/api';
import { ClinicalChip, SourceChip, severityColor } from './clinicalBadges';

type Props = {
  spruceEntityId?: string;
  category: string;
  label: string;
  placeholder?: string;
  emptyText?: string;
  /** chip accent colour (defaults to a neutral outline). */
  accent?: string;
  /** Hide the internal label row when the parent (e.g. a card header) already
   *  shows the title; the Add control stays. */
  hideLabel?: boolean;
  /** Suppress the entire header row (label AND add button) — for a secondary
   *  list that flows directly under a sibling list's header/divider (e.g. past
   *  events under the HX header). Items remain editable via their ⋮ menus. */
  hideHeader?: boolean;
  /** When set (e.g. "Directions" for meds), the add/edit form shows a second
   *  field and items are stored as "name — secondary" so name + sig display on
   *  separate lines. */
  secondaryLabel?: string;
  /** Allergy mode: parse the tail as "severity (reactions)" and render a
   *  colour-coded severity chip with the reactions muted. */
  severityBadge?: boolean;
  /** Called with the item count after each load (for "(N)" panel titles). */
  onCount?: (n: number) => void;
};

// Split an allergy tail ("Severe (Anaphylaxis)") into severity + reactions.
const splitSeverity = (tail: string): { severity: string; reactions: string } => {
  const rx = /\(([^)]*)\)/.exec(tail);
  const reactions = rx ? rx[1].trim() : '';
  const severity = tail.replace(/\([^)]*\)/, '').replace(/[—-]\s*/g, ' ').trim();
  return { severity, reactions };
};

// One shared event so every instance (snapshot pill, details bar, mobile sheet)
// of a given category refreshes together when an item is added/edited/removed.
const DETAIL_EVENT = 'patientDetailChange';

// Many imported items read "Name — qualifier / sig". Show the name with light
// emphasis and the remainder as muted secondary text for scannability.
const SIG_VERB = /\b(take|apply|instill|inhale|use|inject|place|chew|spray|swish|insert|give|dissolve|rinse)\b/i;
const splitContent = (content: string): { head: string; tail: string } => {
  const idx = content.indexOf('\u2014'); // em dash
  if (idx > 0) return { head: content.slice(0, idx).trim(), tail: content.slice(idx + 1).trim() };
  const m = SIG_VERB.exec(content);
  if (m && m.index > 0) return { head: content.slice(0, m.index).trim(), tail: content.slice(m.index).trim() };
  return { head: content.trim(), tail: '' };
};

// Some Practice Fusion imports arrive as one blob with literal "\n" escape
// sequences and a leading "[Category Name]" tag (e.g. "[Ongoing Medical
// Problems] SVT\nAnxiety\n…"). Display-only normalization: unescape to real
// line breaks and drop the redundant tag — the stored item is untouched.
const unescapeNewlines = (raw: string) => raw.replace(/\\n/g, '\n');
const normalizeContent = (raw: string): string =>
  unescapeNewlines(raw).replace(/^\s*\[[^\]]*\]\s*/, '').trim();

/** Long imported HX blobs: show this many lines when collapsed. */
const COLLAPSED_LINES = 3;

// Density pass: one type-step smaller across the board.
const labelSx = {
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  fontWeight: 600,
  fontSize: '0.64rem',
  lineHeight: 1.2,
};

const PatientDetailListManager: React.FC<Props> = ({
  spruceEntityId,
  category,
  label,
  placeholder,
  emptyText = 'None recorded',
  hideLabel = false,
  hideHeader = false,
  secondaryLabel,
  severityBadge = false,
  onCount,
}) => {
  const [list, setList] = useState<PatientDetailItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [sigDraft, setSigDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const [menuItem, setMenuItem] = useState<PatientDetailItem | null>(null);
  // Meds/allergies-with-sig: hide second-line directions by default (one list-wide toggle).
  const [showDirections, setShowDirections] = useState(false);
  // Long multiline HX blobs: expand/collapse per item (default collapsed).
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    if (!spruceEntityId) return;
    setLoading(true);
    try {
      const res = await clinicalApi.listDetails(spruceEntityId, category);
      const rows = res.data?.data || [];
      setList(rows);
      onCount?.(rows.length);
    } catch {
      setList([]);
      onCount?.(0);
    } finally {
      setLoading(false);
    }
  }, [spruceEntityId, category, onCount]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      if (detail.category && detail.category !== category) return;
      if (detail.spruceEntityId && detail.spruceEntityId !== spruceEntityId) return;
      load();
    };
    window.addEventListener(DETAIL_EVENT, onChange);
    return () => window.removeEventListener(DETAIL_EVENT, onChange);
  }, [load, spruceEntityId, category]);

  const announce = () => {
    try {
      window.dispatchEvent(new CustomEvent(DETAIL_EVENT, { detail: { spruceEntityId, category } }));
    } catch {
      /* noop */
    }
  };

  const startAdd = () => {
    setEditId(null);
    setDraft('');
    setSigDraft('');
    setAdding(true);
  };
  const cancelEntry = () => {
    setAdding(false);
    setEditId(null);
    setDraft('');
    setSigDraft('');
  };

  const submitEntry = async () => {
    const name = draft.trim();
    if (!spruceEntityId || !name) return;
    setSaving(true);
    try {
      // Bulk add: several lines pasted into the primary field become one item
      // each (the secondary/sig field is ignored in that case).
      const lines = name.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
      if (!editId && lines.length > 1) {
        for (const line of lines) await clinicalApi.addDetailItem(spruceEntityId, category, line);
      } else {
        const sig = sigDraft.trim();
        const content = secondaryLabel && sig ? `${name} — ${sig}` : name;
        if (editId) await clinicalApi.updateDetailItem(editId, content);
        else await clinicalApi.addDetailItem(spruceEntityId, category, content);
      }
      cancelEntry();
      await load();
      announce();
    } catch {
      /* keep the editor open on failure */
    } finally {
      setSaving(false);
    }
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>, item: PatientDetailItem) => {
    setMenuEl(e.currentTarget);
    setMenuItem(item);
  };
  const closeMenu = () => {
    setMenuEl(null);
    setMenuItem(null);
  };
  const beginEdit = () => {
    if (!menuItem) return;
    setAdding(false);
    setEditId(menuItem.id);
    // Unescape literal "\n" so imported blobs edit as real lines, not escapes.
    const raw = unescapeNewlines(menuItem.content);
    if (secondaryLabel) {
      const { head, tail } = splitContent(raw);
      setDraft(head);
      setSigDraft(tail);
    } else {
      setDraft(raw);
      setSigDraft('');
    }
    closeMenu();
  };
  const removeItem = async () => {
    if (!menuItem) return;
    const id = menuItem.id;
    closeMenu();
    try {
      await clinicalApi.deleteDetailItem(id);
      await load();
      announce();
    } catch {
      /* noop */
    }
  };

  if (!spruceEntityId) return null;

  const editorOpen = adding || editId !== null;

  return (
    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {!hideHeader && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: hideLabel ? 'flex-end' : 'space-between' }}>
          {!hideLabel && (
            <Typography variant="caption" sx={labelSx}>
              {label}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {loading && <CircularProgress size={12} />}
            {secondaryLabel && list.length > 0 && (
              <Typography
                component="button"
                type="button"
                onClick={() => setShowDirections((v) => !v)}
                sx={{
                  border: 0,
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  color: 'primary.main',
                  fontWeight: 600,
                  fontSize: '0.68rem',
                  lineHeight: 1.2,
                  px: 0.25,
                  whiteSpace: 'nowrap',
                }}
              >
                {showDirections ? 'Hide directions' : 'Show directions'}
              </Typography>
            )}
            <Tooltip title={`Add ${label.toLowerCase()}`}>
              <IconButton size="small" onClick={() => (editorOpen ? cancelEntry() : startAdd())} sx={{ p: 0.25 }}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {editorOpen && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <TextField
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={
              secondaryLabel
                ? `${label} name (or paste one per line to add several)`
                : placeholder || `Add ${label.toLowerCase()}…`
            }
            size="small"
            fullWidth
            multiline
            maxRows={4}
            autoFocus
            disabled={saving}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submitEntry();
              }
              if (e.key === 'Escape') cancelEntry();
            }}
          />
          {secondaryLabel && (
            <TextField
              value={sigDraft}
              onChange={(e) => setSigDraft(e.target.value)}
              placeholder={`${secondaryLabel} (optional) — e.g. Take 1 tablet by mouth daily`}
              size="small"
              fullWidth
              multiline
              maxRows={3}
              disabled={saving}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submitEntry();
                }
                if (e.key === 'Escape') cancelEntry();
              }}
            />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Typography
              component="button"
              type="button"
              onClick={cancelEntry}
              sx={{ border: 0, bgcolor: 'transparent', cursor: 'pointer', color: 'text.secondary', fontSize: '0.8rem' }}
            >
              Cancel
            </Typography>
            <Typography
              component="button"
              type="button"
              onClick={submitEntry}
              sx={{ border: 0, bgcolor: 'transparent', cursor: 'pointer', color: 'primary.main', fontWeight: 600, fontSize: '0.8rem' }}
            >
              {saving ? 'Saving…' : editId ? 'Save' : 'Add'}
            </Typography>
          </Box>
        </Box>
      )}

      {list.length === 0 && !editorOpen ? (
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          {emptyText}
        </Typography>
      ) : (
        // Aligned rows: name (bold) with directions/sig on a muted second line.
        // Reads like a clinical list, denser than the old wrapping chips.
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {list.map((item, i) => {
            const content = normalizeContent(item.content);
            // Multi-line imports read as a list already — keep the lines intact
            // instead of head/sig splitting (which assumes a single entry).
            const multiline = content.includes('\n');
            const { head, tail } = multiline ? { head: content, tail: '' } : splitContent(content);
            const sev = severityBadge && tail ? splitSeverity(tail) : null;
            const lines = multiline ? head.split('\n') : [];
            const canCollapse = multiline && lines.length > COLLAPSED_LINES;
            const isExpanded = !!expandedItems[item.id];
            const displayHead =
              canCollapse && !isExpanded ? lines.slice(0, COLLAPSED_LINES).join('\n') : head;
            // Soft-wrap denser type for long HX-style blobs; single-line rows stay scannable.
            const primarySx = multiline
              ? {
                  fontWeight: 500 as const,
                  color: 'text.primary',
                  fontSize: '0.70rem',
                  lineHeight: 1.32,
                  letterSpacing: '-0.01em',
                  minWidth: 0,
                  wordBreak: 'break-word' as const,
                  overflowWrap: 'anywhere' as const,
                  whiteSpace: 'pre-line' as const,
                }
              : {
                  fontWeight: 600 as const,
                  color: 'text.primary',
                  fontSize: '0.76rem',
                  lineHeight: 1.25,
                  minWidth: 0,
                  wordBreak: 'break-word' as const,
                  whiteSpace: 'pre-line' as const,
                };
            return (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.5,
                  py: multiline ? 0.3 : 0.35,
                  borderTop: i ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6, flexWrap: 'wrap' }}>
                    <Typography component="span" sx={primarySx}>
                      {displayHead}
                    </Typography>
                    {sev?.severity && (
                      <ClinicalChip label={sev.severity} color={severityColor(sev.severity)} />
                    )}
                    <SourceChip source={item.source} />
                  </Box>
                  {canCollapse && (
                    <Typography
                      component="button"
                      type="button"
                      onClick={() =>
                        setExpandedItems((prev) => ({ ...prev, [item.id]: !prev[item.id] }))
                      }
                      sx={{
                        border: 0,
                        bgcolor: 'transparent',
                        cursor: 'pointer',
                        color: 'primary.main',
                        fontWeight: 600,
                        fontSize: '0.66rem',
                        lineHeight: 1.2,
                        mt: 0.25,
                        px: 0,
                      }}
                    >
                      {isExpanded
                        ? 'Show less'
                        : `Show full (${lines.length - COLLAPSED_LINES} more lines)`}
                    </Typography>
                  )}
                  {sev ? (
                    sev.reactions && (
                      <Typography
                        sx={{ display: 'block', color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.28, mt: 0.1 }}
                      >
                        {sev.reactions}
                      </Typography>
                    )
                  ) : (
                    // Directions / sig: list-wide collapse when secondaryLabel is set.
                    // (Without secondaryLabel, keep prior always-on tail for non-med lists.)
                    tail && (secondaryLabel ? showDirections : true) && (
                      <Typography
                        sx={{ display: 'block', color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.28, mt: 0.08, wordBreak: 'break-word' }}
                      >
                        {tail}
                      </Typography>
                    )
                  )}
                </Box>
                <IconButton
                  size="small"
                  aria-label="Edit or remove"
                  onClick={(e) => openMenu(e, item)}
                  sx={{ p: 0.25, mt: -0.25, flexShrink: 0 }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={closeMenu}>
        <MenuItem onClick={beginEdit}>Edit</MenuItem>
        <MenuItem onClick={removeItem} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default PatientDetailListManager;
